/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useProviderActions.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for provider actions (test connection, refresh capabilities, refresh voices).
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import {
  testAiProviderConnection,
  refreshAiProviderCapabilities,
  refreshAiProviderVoices
} from '../api/aiProvidersApi';
import type { AiProviderForm } from './useProviderData';

function mapFormToPayload(form: AiProviderForm) {
  return {
    domain_id: form.domainId,
    provider: form.provider,
    name: form.name,
    api_key: form.apiKey,
    base_url: form.baseUrl ? form.baseUrl : null,
    organization: form.organization ? form.organization : null,
    api_key_type: ['azure', 'google'].includes(form.provider) && form.azureApiKeyType ? form.azureApiKeyType : null,
    capabilities: form.capabilities,
    status: form.status
  };
}

export function useProviderActions(
  formData: AiProviderForm,
  mode: 'create' | 'edit',
  id: string | undefined
) {
  const { t } = useI18n();
  const { showError, showSuccess } = useNotifications();

  const [testingConnection, setTestingConnection] = useState(false);
  const [refreshingCapabilities, setRefreshingCapabilities] = useState(false);
  const [refreshingVoices, setRefreshingVoices] = useState(false);

  const showErrorRef = useRef(showError);
  const translateRef = useRef(t);

  useEffect(() => {
    showErrorRef.current = showError;
    translateRef.current = t;
  }, [showError, t]);

  // Test connection
  const handleTestConnection = useCallback(async () => {
    if (!formData.apiKey) {
      showErrorRef.current?.(translateRef.current('aiProviders.messages.apiKeyRequired'));
      return;
    }

    setTestingConnection(true);
    try {
      const response = await testAiProviderConnection(mapFormToPayload(formData));
      showSuccess(response?.message || translateRef.current('aiProviders.messages.connectionSuccess'));
    } catch (error) {
      const message =
        error instanceof Error && error.message && error.message !== 'REQUEST_FAILED'
          ? error.message
          : translateRef.current('aiProviders.messages.connectionError');
      showErrorRef.current?.(message);
    } finally {
      setTestingConnection(false);
    }
  }, [formData, showSuccess]);

  // Refresh capabilities
  const handleRefreshCapabilities = useCallback(async () => {
    if (mode !== 'edit' || !id) return;

    if (!formData.apiKey) {
      showErrorRef.current?.(translateRef.current('aiProviders.messages.apiKeyRequired'));
      return;
    }

    setRefreshingCapabilities(true);
    try {
      const response = await refreshAiProviderCapabilities(id);
      showSuccess(response?.message || translateRef.current('aiProviders.messages.updateSuccess'));
    } catch (error) {
      const message =
        error instanceof Error && error.message && error.message !== 'REQUEST_FAILED'
          ? error.message
          : translateRef.current('aiProviders.messages.saveError');
      showErrorRef.current?.(message);
    } finally {
      setRefreshingCapabilities(false);
    }
  }, [formData.apiKey, id, mode, showSuccess]);

  // Refresh voices
  const handleRefreshVoices = useCallback(async () => {
    if (mode !== 'edit' || !id) return;

    if (!formData.apiKey) {
      showErrorRef.current?.(translateRef.current('aiProviders.messages.apiKeyRequired'));
      return;
    }

    setRefreshingVoices(true);
    try {
      const response = await refreshAiProviderVoices(id);
      showSuccess(response?.message || translateRef.current('aiProviders.messages.updateSuccess'));
    } catch (error) {
      const message =
        error instanceof Error && error.message && error.message !== 'REQUEST_FAILED'
          ? error.message
          : translateRef.current('aiProviders.messages.saveError');
      showErrorRef.current?.(message);
    } finally {
      setRefreshingVoices(false);
    }
  }, [formData.apiKey, id, mode, showSuccess]);

  return {
    testingConnection,
    refreshingCapabilities,
    refreshingVoices,
    handleTestConnection,
    handleRefreshCapabilities,
    handleRefreshVoices
  };
}

